import { useState, useMemo, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useThrottledCallback } from '@react-hookz/web';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { NoteIcon, PlusIcon, FileTextIcon } from '@phosphor-icons/react';
import type {
  MemoryBankNoteMetadata,
  CreateNoteInput,
  NoteUsage,
  MemoryBankNote,
  UpdateNoteInput,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import {
  useCreateMemoryBankNote,
  useUpdateMemoryBankNote,
} from '@/api/core/ai/memory-bank/mutations';
import { useListNoteUsages } from '@/api/core/ai/memory-bank/queries';
import { NoteEditForm } from './NoteEditForm';
import { NotesUsedDisplay } from './NotesUsedDisplay';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import { SystemEntity } from '@erp/shared-types';
import CustomerPill from '@/components/customer/CustomerPill';
import SupplierPill from '@/components/company/SupplierPill';

export interface MemoryBankNotesProps {
  staticMetadata?: Partial<MemoryBankNoteMetadata>;
  dynamicMetadata?: Partial<MemoryBankNoteMetadata>;
  /**
   * Note usages to display. If entityId and entityType are provided, this will be fetched automatically
   */
  notesUsed?: NoteUsage[];
  /**
   * Max number of notes to show in the compact view
   */
  maxVisible?: number;
  /**
   * Size of the icon
   */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Called when notes change (create, update, delete)
   */
  onNotesChange?: () => void;
  /**
   * Entity ID to link notes to - when provided with entityType, will fetch usages automatically
   */
  entityId: string;
  /**
   * Entity type to link notes to - when provided with entityId, will fetch usages automatically
   */
  entityType: MemoryBankSupportedEntityTypes;
  /**
   * Optional tooltip text to explain what this feature does
   */
  tooltipText?: string;
}

// Icon size mapping
const iconSizes = {
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-7 w-7',
};

// Badge size mapping
const badgeSizes = {
  sm: 'h-3.5 w-3.5 text-[9px]',
  md: 'h-4 w-4 text-[10px]',
  lg: 'h-5 w-5 text-xs',
};

// Edit mode state type
type EditMode =
  | { mode: 'view' }
  | { mode: 'create' }
  | { mode: 'edit'; note: MemoryBankNote };

function getEntityTypeName(
  entityType: MemoryBankSupportedEntityTypes,
  gt: ReturnType<typeof useGT>,
): string {
  const entityMap: Record<SystemEntity, string> = {
    [SystemEntity.AR_CUSTOMER]: gt('customer', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_CUSTOMER_PRODUCT]: gt('customer product', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_STAGED_SALES_ORDER]: gt('sales order', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_DRAFT_SALES_ORDER]: gt('sales order', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.IM_TRANSFER_ORDER]: gt('transfer order', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM]: gt('transfer order line item', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AP_INVOICE]: gt('invoice', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AP_SUPPLIER]: gt('supplier', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CORE_DOCUMENT]: gt('document', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.WORKFLOW_RUN]: gt('workflow run', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.STEP_RUN]: gt('step run', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.INBOUND_EMAIL]: gt('email', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.OUTBOUND_EMAIL]: gt('email', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CORE_MEMORY_BANK_NOTE]: gt('note', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CORE_MEMORY_BANK_NOTE_USAGE]: gt('note usage', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.ORGANIZATION]: gt('organization', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CORE_DOCUMENT_OCR_RESULT]: gt('OCR result', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AP_INVOICE_LINE_ITEM]: gt('invoice line item', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.INTERNAL_PURCHASE_ORDER]: gt('purchase order', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: gt('PO line item', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.PAYMENT]: gt('payment', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.FINANCIAL_TRANSACTION]: gt('transaction', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CHARGE]: gt('charge', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.WORKFLOW_DEFINITION]: gt('workflow', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_SALES_ORDER]: gt('sales order', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_SALES_ORDER_LINE_ITEM]: gt('sales order line item', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.SCM_SHIPMENT]: gt('shipment', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AI_AGENT_RUN]: gt('agent run', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.TASK]: gt('task', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_PAYMENT]: gt('AR payment', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_INVOICE]: gt('AR invoice', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_PAYMENT_APPLICATION]: gt('payment application', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_REMITTANCE_ADVICE]: gt('remittance advice', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_SHIPMENT]: gt('AR shipment', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_COLLECTION_THREAD]: gt('collection thread', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AR_SAVED_REPORT]: gt('AR report', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.EXTERNAL_SYSTEM_REFERENCE]: gt('external reference', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.AI_EXTRACTION_RESULT]: gt('AI extraction result', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.CORE_COMPANY]: gt('company', {
      context: 'memory bank entity type label',
    }),
    [SystemEntity.REBATE_AGREEMENT]: gt('rebate agreement', {
      context: 'memory bank entity type label',
    }),
  };

  return (
    entityMap[entityType as SystemEntity] ||
    gt('item', { context: 'generic entity' })
  );
}

// Component to render entity pills based on type
function EntityPill({
  entityType,
  entityId,
}: {
  entityType: MemoryBankSupportedEntityTypes;
  entityId: string;
}) {
  switch (entityType as SystemEntity) {
    case SystemEntity.AR_CUSTOMER:
      return <CustomerPill customerId={entityId} />;
    case SystemEntity.AP_SUPPLIER:
      return <SupplierPill supplierId={entityId} />;
    // Add more entity types as needed
    default:
      return null;
  }
}

// Empty state component
function EmptyState({
  entityType,
  onAddNote,
  gt,
}: {
  entityType: MemoryBankSupportedEntityTypes;
  onAddNote: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  const entityName = getEntityTypeName(entityType, gt);

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-3 p-3 rounded-full bg-gray-100">
        <FileTextIcon className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-base mb-1">
        <T>No notes yet</T>
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-xs">
        <T>
          Add notes to guide AI workflows for this <Var>{entityName}</Var>.
          Notes help provide context and improve automation accuracy.
        </T>
      </p>
      <Button onClick={onAddNote} size="sm">
        <PlusIcon className="h-4 w-4 mr-1" />
        <T>Add your first note</T>
      </Button>
    </div>
  );
}

export function MemoryBankNotes({
  staticMetadata = {},
  dynamicMetadata = {},
  notesUsed: providedNotesUsed,
  className,
  onNotesChange,
  entityId,
  entityType,
  tooltipText,
  size = 'md',
}: MemoryBankNotesProps) {
  const gt = useGT();
  // Unified edit mode state
  const [editMode, setEditMode] = useState<EditMode>({ mode: 'view' });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showFirstTimeAnimation, setShowFirstTimeAnimation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { showToast } = useToast();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch note usages if entityId and entityType are provided
  const { data: fetchedUsagesData, isLoading: isLoadingUsages } =
    useListNoteUsages(entityId && entityType ? { entityId, entityType } : null);

  // Use provided notesUsed or fetched usages
  const notesUsed = providedNotesUsed ?? fetchedUsagesData?.usages ?? [];

  const invalidateListQuery = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.list.key(),
      orpc.memoryBank.batchGet.key({
        input: { ids: notesUsed.map((usage) => usage.noteId) },
      }),
      ...(entityId && entityType
        ? [
            orpc.memoryBank.listUsages.key({
              input: { entityId, entityType },
            }),
          ]
        : []),
    ],
    [notesUsed, entityId, entityType],
  );

  // Check if this is the user's first time seeing notes for this entity
  useEffect(() => {
    const storageKey = `memorybank-seen-${entityType}`;
    const hasSeen = localStorage.getItem(storageKey);
    if (!hasSeen && notesUsed.length === 0) {
      setShowFirstTimeAnimation(true);
    }
  }, [entityType, notesUsed.length]);

  // Mark as seen when user interacts
  const markAsSeen = () => {
    const storageKey = `memorybank-seen-${entityType}`;
    localStorage.setItem(storageKey, 'true');
    setShowFirstTimeAnimation(false);
  };

  const noteCount = notesUsed.length;
  const defaultTooltipContent = isLoadingUsages
    ? gt('Loading notes...')
    : noteCount === 0
      ? gt('Add AI workflow notes')
      : noteCount === 1
        ? gt('1 note', { context: 'memory bank notes count tooltip' })
        : gt('{count} notes', {
            context: 'memory bank notes count tooltip',
            count: String(noteCount),
          });
  const notesTooltipContent = tooltipText || defaultTooltipContent;

  // Merge metadata and filter out null/undefined values
  const mergedMetadata = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          ...staticMetadata,
          ...dynamicMetadata,
        }).filter(([, value]) => value !== undefined && value !== null),
      ),
    [staticMetadata, dynamicMetadata],
  );

  const { mutateAsync: createNote, isPending: isCreatingNote } =
    useCreateMemoryBankNote();
  const { mutateAsync: updateNote, isPending: isUpdatingNote } =
    useUpdateMemoryBankNote();

  const handleCreateOrUpdateNoteInternal = async (
    noteContent: string,
    filteredMetadata: Partial<MemoryBankNoteMetadata>,
  ) => {
    try {
      if (editMode.mode === 'edit') {
        const input: UpdateNoteInput = {
          note: noteContent,
          metadata: filteredMetadata as MemoryBankNoteMetadata,
        };

        await updateNote({ id: editMode.note.id, data: input });
        showToast({
          message: gt('Note updated successfully'),
          color: 'success',
          icon: 'check',
        });
      } else {
        const input: CreateNoteInput = {
          note: noteContent,
          metadata: filteredMetadata as MemoryBankNoteMetadata,
          entityId,
          entityType,
        };

        await createNote(input);
        showToast({
          message: gt('Note created successfully'),
          color: 'success',
          icon: 'check',
        });
      }

      // Close the editor and trigger refetch on success
      setEditMode({ mode: 'view' });
      handleNoteChange();
    } catch (error) {
      showToast({
        message: gt('Failed to save note'),
        color: 'error',
        icon: 'error',
      });
      throw error;
    }
  };

  // Throttle the create/update handler to prevent rapid consecutive mutations
  // Uses leading edge execution with 1 second wait time
  const handleCreateOrUpdateNote = useThrottledCallback(
    handleCreateOrUpdateNoteInternal,
    [handleCreateOrUpdateNoteInternal],
    1000,
    true,
  );

  const handleCancel = () => {
    setEditMode({ mode: 'view' });
  };

  const handleNoteChange = () => {
    invalidateListQuery();
    onNotesChange?.();
  };

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);
    if (open) {
      markAsSeen();
    }
    // Reset to view mode when closing popover
    if (!open) {
      setEditMode({ mode: 'view' });
    }
  };

  const handleStartEdit = (mode: EditMode) => {
    setEditMode(mode);
    // On mobile, close popover when entering edit mode (Dialog will open)
    if (isMobile && mode.mode !== 'view') {
      setIsPopoverOpen(false);
    }
  };

  const hasNotes = notesUsed.length > 0;
  const isPending = isCreatingNote || isUpdatingNote;

  return (
    <>
      <div className={cn('relative inline-flex', className)}>
        <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'relative inline-flex items-center justify-center',
                'transition-all duration-200',
                'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 rounded',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                showFirstTimeAnimation && 'animate-pulse',
              )}
              disabled={isLoadingUsages}
              aria-label={gt('Memory bank notes, {count} notes', {
                context: 'aria-label for memory bank notes button',
                count: String(notesUsed.length),
              })}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative">
                    <NoteIcon
                      className={cn(
                        iconSizes[size],
                        hasNotes
                          ? 'fill-gray-200 text-gray-600'
                          : 'text-gray-400',
                        'transition-colors duration-200',
                      )}
                    />
                    {isLoadingUsages && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                      </div>
                    )}
                    {!isLoadingUsages && hasNotes && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'absolute -top-1 -right-1 flex items-center justify-center',
                          'bg-gray-600 text-white hover:bg-gray-600',
                          badgeSizes[size],
                          'p-0 min-w-0 border-2 border-white',
                        )}
                      >
                        {notesUsed.length > 9 ? '9+' : notesUsed.length}
                      </Badge>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{notesTooltipContent}</p>
                </TooltipContent>
              </Tooltip>
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-[500px] p-0" align="start">
            {/* Header */}
            <div className="border-b p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {editMode.mode === 'view' ? (
                    <>
                      <h3 className="font-semibold text-sm">
                        <T>Memory Bank Notes</T>
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <p className="text-xs text-muted-foreground">
                          <T>Notes for</T>
                        </p>
                        {entityId && entityType && (
                          <EntityPill
                            entityType={entityType}
                            entityId={entityId}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="font-semibold text-sm">
                        {editMode.mode === 'create' ? (
                          <T>New Note</T>
                        ) : (
                          <T>Edit Note</T>
                        )}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        <T>Notes</T>
                        {' > '}
                        {editMode.mode === 'create' ? <T>New</T> : <T>Edit</T>}
                      </p>
                    </>
                  )}
                </div>
                {editMode.mode === 'view' && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleStartEdit({ mode: 'create' })}
                    disabled={isPending}
                    className="flex-shrink-0"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    <T>Add note</T>
                  </Button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto">
              {isLoadingUsages ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-full bg-gray-200" />
                  <Skeleton className="h-4 w-3/4 bg-gray-200" />
                  <Skeleton className="h-20 w-full bg-gray-200" />
                </div>
              ) : !isMobile &&
                (editMode.mode === 'create' || editMode.mode === 'edit') ? (
                <div className="p-3">
                  <NoteEditForm
                    initialValue={
                      editMode.mode === 'edit' ? editMode.note.note : undefined
                    }
                    metadata={
                      editMode.mode === 'edit'
                        ? editMode.note.metadata
                        : mergedMetadata
                    }
                    onSave={handleCreateOrUpdateNote}
                    onCancel={handleCancel}
                    isSubmitting={isPending}
                    placeholder=""
                    entityType={entityType}
                    entityId={entityId}
                  />
                </div>
              ) : !hasNotes ? (
                <EmptyState
                  entityType={entityType}
                  onAddNote={() => handleStartEdit({ mode: 'create' })}
                  gt={gt}
                />
              ) : (
                <div className="p-3">
                  <NotesUsedDisplay
                    notesUsed={notesUsed}
                    onEditNote={(note) =>
                      handleStartEdit({ mode: 'edit', note })
                    }
                    metadata={mergedMetadata}
                    entityType={entityType}
                    entityId={entityId}
                  />
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Edit Dialog for mobile */}
      {isMobile && (
        <Dialog
          open={editMode.mode !== 'view'}
          onOpenChange={(open) => {
            if (!open) {
              setEditMode({ mode: 'view' });
            }
          }}
        >
          <DialogContent className="max-w-[95vw]">
            <DialogHeader>
              <DialogTitle>
                {editMode.mode === 'create' ? (
                  <T>New Note</T>
                ) : (
                  <T>Edit Note</T>
                )}
              </DialogTitle>
              <DialogDescription>
                <T>
                  Add notes to guide AI workflows for this{' '}
                  <Var>{getEntityTypeName(entityType, gt)}</Var>
                </T>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {editMode.mode !== 'view' && (
                <NoteEditForm
                  initialValue={
                    editMode.mode === 'edit' ? editMode.note.note : undefined
                  }
                  metadata={
                    editMode.mode === 'edit'
                      ? editMode.note.metadata
                      : mergedMetadata
                  }
                  onSave={handleCreateOrUpdateNote}
                  onCancel={handleCancel}
                  isSubmitting={isPending}
                  placeholder=""
                  entityType={entityType}
                  entityId={entityId}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
